import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDuration } from '../../utils/monitoringFormat';
import { parseApiBlobError } from '../../utils/parseApiBlobError';
import RemoteSupportSessionsTab from '../../components/workforceMonitoring/RemoteSupportSessionsTab';

const DISPLAY_TZ = 'America/Chicago';

type DailyReportMode = 'today_so_far' | 'final';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function formatTimestamp(value?: string | null): string {
  if (!value) return '—';
  return moment(value).tz(DISPLAY_TZ).format('h:mm A');
}

function emailStatusLabel(status?: string): string {
  switch (status) {
    case 'not_enabled': return 'Not Enabled';
    case 'scheduled': return 'Scheduled';
    case 'generated': return 'Generated';
    case 'sent': return 'Sent';
    case 'failed': return 'Failed';
    default: return status ? status.replace(/_/g, ' ') : 'Unknown';
  }
}

function emailStatusColor(status?: string): 'default' | 'info' | 'success' | 'error' | 'warning' {
  switch (status) {
    case 'sent': return 'success';
    case 'failed': return 'error';
    case 'scheduled': return 'info';
    case 'generated': return 'warning';
    default: return 'default';
  }
}

const KpiCard: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5">{value}</Typography>
    </CardContent>
  </Card>
);

const PayrollReconciliationTab: React.FC = () => {
  const [workDate, setWorkDate] = useState(moment().format('YYYY-MM-DD'));
  const [approvedHours, setApprovedHours] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});

  const { data, refetch } = useQuery({
    queryKey: ['payrollReconciliation', workDate],
    queryFn: () => apiClient.monitoring.getPayrollReconciliation({ date: workDate }),
  });

  const rows = data?.data?.data || [];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Compare scheduled, clock, and recorded work sessions. Approved payroll is manual — TEKS TIME never overwrites official payroll automatically.
      </Typography>
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField size="small" label="Date" type="date" value={workDate} onChange={(e) => setWorkDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Button variant="outlined" onClick={() => refetch()}>Refresh</Button>
        <Button variant="outlined" onClick={() => { window.location.href = `/api/monitoring/export/payroll-reconciliation?date=${workDate}`; }}>Export CSV</Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Scheduled</TableCell>
              <TableCell>Clock Records</TableCell>
              <TableCell>Work Sessions</TableCell>
              <TableCell>Locked Idle</TableCell>
              <TableCell>Lunch</TableCell>
              <TableCell>Suggested Review</TableCell>
              <TableCell>Approved Payroll</TableCell>
              <TableCell>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: any) => (
              <TableRow key={row.employee_id}>
                <TableCell>{row.employee_id}</TableCell>
                <TableCell>{formatDuration(row.scheduled_seconds)}</TableCell>
                <TableCell>{formatDuration(row.clocked_seconds)}</TableCell>
                <TableCell>{formatDuration(row.work_session_seconds)}</TableCell>
                <TableCell>{formatDuration(row.locked_idle_seconds)}</TableCell>
                <TableCell>{formatDuration(row.lunch_seconds)}</TableCell>
                <TableCell>{formatDuration(row.work_session_seconds)}</TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder="8:00"
                    value={approvedHours[row.employee_id] || ''}
                    onChange={(e) => setApprovedHours({ ...approvedHours, [row.employee_id]: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder="Reason"
                    value={reasons[row.employee_id] || ''}
                    onChange={(e) => setReasons({ ...reasons, [row.employee_id]: e.target.value })}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {rows.some((row: any) => row.manual_review_required) && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Some rows differ between clock records and recorded work sessions by more than 5 minutes and should be reviewed by a supervisor.
        </Alert>
      )}
    </Box>
  );
};

const DailyActivityTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canRead = permissions.includes('monitoring.activity_reports.read');
  const canExport = permissions.includes('monitoring.activity_reports.export');
  const canEmail = permissions.includes('monitoring.activity_reports.email');

  const [reportDate, setReportDate] = useState(moment().format('YYYY-MM-DD'));
  const [reportMode, setReportMode] = useState<DailyReportMode | undefined>(undefined);
  const [groupBy, setGroupBy] = useState<'employee' | 'computer'>('employee');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);

  const isToday = reportDate === moment().format('YYYY-MM-DD');
  const effectiveMode: DailyReportMode = reportMode || (isToday ? 'today_so_far' : 'final');

  useEffect(() => {
    setReportMode(undefined);
  }, [reportDate]);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['dailyActivityReport', reportDate, groupBy, reportMode],
    queryFn: () => apiClient.monitoring.getDailyActivityReport({
      date: reportDate,
      group_by: groupBy,
      ...(reportMode ? { mode: reportMode } : {}),
    }),
    enabled: canRead,
  });

  const { data: emailStatusData } = useQuery({
    queryKey: ['dailyActivityEmailStatus', reportDate],
    queryFn: () => apiClient.monitoring.getDailyActivityEmailStatus({ date: reportDate }),
    enabled: canRead,
  });

  const sendMutation = useMutation({
    mutationFn: () => apiClient.monitoring.sendDailyActivityEmail({
      date: reportDate,
      mode: effectiveMode,
      force_resend: emailStatusData?.data?.data?.status === 'sent',
    }),
    onSuccess: (response) => {
      const result = response.data?.data;
      setSendConfirmOpen(false);
      setActionError(null);
      if (result?.skipped && result?.reason === 'ALREADY_SENT') {
        setActionSuccess('Report was already sent. Use Resend to send again.');
      } else if (result?.sent) {
        setActionSuccess('Daily workforce report email sent.');
      } else {
        setActionSuccess('Send request completed.');
      }
      queryClient.invalidateQueries({ queryKey: ['dailyActivityEmailStatus', reportDate] });
    },
    onError: (err: any) => {
      setSendConfirmOpen(false);
      setActionSuccess(null);
      setActionError(err?.response?.data?.message || 'Failed to send report email.');
    },
  });

  const report = data?.data?.data;
  const rows = report?.rows || [];
  const summary = report?.summary;
  const emailStatus = emailStatusData?.data?.data;
  const emailAlreadySent = emailStatus?.status === 'sent';

  const summaryColumns = useMemo(() => (
    groupBy === 'employee'
      ? ['Employee', 'Computers', 'Work', 'Active Work', 'Idle', 'Locked', 'Top App', 'Actions', 'Snapshots']
      : ['Computer', 'Employees', 'Active', 'Idle', 'First', 'Last', 'Top App', 'Actions', 'Snapshots']
  ), [groupBy]);

  const handleExport = async () => {
    setExportError(null);
    try {
      const response = await apiClient.monitoring.exportDailyActivityReport({ date: reportDate, group_by: groupBy });
      downloadBlob(response.data, `daily-activity-${reportDate}-${groupBy}.csv`);
    } catch (err) {
      setExportError(await parseApiBlobError(err, 'Export failed'));
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setActionError(null);
    try {
      const response = await apiClient.monitoring.previewDailyActivityEmail({
        date: reportDate,
        mode: effectiveMode,
      });
      setPreviewHtml(response.data.data.html);
      setPreviewOpen(true);
    } catch (err: any) {
      setActionError(err?.response?.data?.message || 'Failed to load email preview.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const renderAttention = (row: any) => {
    if (!Array.isArray(row.attention) || row.attention.length === 0) return null;
    return (
      <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {row.attention.map((reason: string) => (
          <Chip key={reason} size="small" color="warning" variant="outlined" label={reason} />
        ))}
      </Box>
    );
  };

  if (!canRead) {
    return <Alert severity="warning">Daily Monitoring Report requires the monitoring.activity_reports.read permission.</Alert>;
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Summarized recorded activity for a day — applications, domains, and meaningful actions. Click a row to expand details.
      </Typography>

      <Box display="flex" gap={1} mb={1} flexWrap="wrap" alignItems="center">
        {report?.label && (
          <Typography variant="subtitle1">{report.label}</Typography>
        )}
        {(report?.today_so_far || report?.mode === 'today_so_far') && (
          <Chip size="small" color="info" label="Today so far — not the finished report" />
        )}
        {emailStatus && (
          <Chip
            size="small"
            color={emailStatusColor(emailStatus.status)}
            label={`Email: ${emailStatusLabel(emailStatus.status)}`}
          />
        )}
      </Box>

      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={groupBy}
          onChange={(_, value) => value && setGroupBy(value)}
        >
          <ToggleButton value="employee">By Employee</ToggleButton>
          <ToggleButton value="computer">By Computer</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          label="Date"
          type="date"
          value={reportDate}
          onChange={(e) => setReportDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        {isToday && (
          <Button
            variant="outlined"
            onClick={() => setReportMode('today_so_far')}
            disabled={isFetching && reportMode === 'today_so_far'}
          >
            Build Today So Far
          </Button>
        )}
        <Button variant="outlined" onClick={() => refetch()} disabled={isFetching}>Refresh</Button>
        <Button variant="outlined" onClick={() => void handlePreview()} disabled={previewLoading}>
          {previewLoading ? 'Loading…' : 'Preview Email'}
        </Button>
        {canEmail && (
          <Button
            variant="contained"
            onClick={() => {
              setActionSuccess(null);
              setActionError(null);
              setSendConfirmOpen(true);
            }}
            disabled={sendMutation.isPending}
          >
            {emailAlreadySent ? 'Resend Report' : 'Send Report'}
          </Button>
        )}
        {canExport && (
          <Button variant="outlined" onClick={() => void handleExport()}>Export CSV</Button>
        )}
      </Box>

      {exportError && <Alert severity="error" sx={{ mb: 2 }}>{exportError}</Alert>}
      {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
      {actionSuccess && <Alert severity="success" sx={{ mb: 2 }}>{actionSuccess}</Alert>}

      {summary && (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4} md={3} lg={1.5}><KpiCard label="Worked" value={summary.worked ?? 0} /></Grid>
          <Grid item xs={6} sm={4} md={3} lg={1.5}><KpiCard label="Attention" value={summary.needs_attention ?? 0} /></Grid>
          <Grid item xs={6} sm={4} md={3} lg={1.5}><KpiCard label="Active" value={formatDuration(summary.total_active_seconds ?? 0)} /></Grid>
          <Grid item xs={6} sm={4} md={3} lg={1.5}><KpiCard label="Idle" value={formatDuration(summary.total_idle_seconds ?? 0)} /></Grid>
          <Grid item xs={6} sm={4} md={3} lg={1.5}><KpiCard label="Locked" value={formatDuration(summary.total_locked_seconds ?? 0)} /></Grid>
          <Grid item xs={6} sm={4} md={3} lg={1.5}><KpiCard label="Actions" value={summary.total_actions ?? 0} /></Grid>
          <Grid item xs={6} sm={4} md={3} lg={1.5}><KpiCard label="Snapshots" value={summary.total_snapshots ?? 0} /></Grid>
        </Grid>
      )}

      {isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                {summaryColumns.map((label) => (
                  <TableCell key={label}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={summaryColumns.length + 1}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No recorded activity for this date.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((row: any) => {
                const key = String(row.group_key);
                const expanded = expandedKey === key;
                const actions = row.meaningful_actions || {};
                return (
                  <React.Fragment key={key}>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpandedKey(expanded ? null : key)}>
                      <TableCell>
                        <IconButton size="small" aria-label={expanded ? 'Collapse row' : 'Expand row'}>
                          {expanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      {groupBy === 'employee' ? (
                        <>
                          <TableCell>
                            <Typography variant="body2">{row.employee_name || row.employee_id || '—'}</Typography>
                            {renderAttention(row)}
                          </TableCell>
                          <TableCell>{Array.isArray(row.computers) ? row.computers.join(', ') : '—'}</TableCell>
                          <TableCell>{formatDuration(row.work_seconds)}</TableCell>
                          <TableCell>
                            {row.active_work_ratio != null ? `${row.active_work_ratio}%` : '—'}
                            <Typography variant="caption" display="block" color="text.secondary">
                              {formatDuration(row.active_seconds)}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatDuration(row.idle_seconds)}</TableCell>
                          <TableCell>{formatDuration(row.locked_seconds)}</TableCell>
                          <TableCell>{row.most_used_app || '—'}</TableCell>
                          <TableCell>{actions.total ?? '—'}</TableCell>
                          <TableCell>{row.snapshot_count ?? 0}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            <Typography variant="body2">{row.computer_name || row.device_id || '—'}</Typography>
                            {renderAttention(row)}
                          </TableCell>
                          <TableCell>{Array.isArray(row.employees) ? row.employees.length : 0}</TableCell>
                          <TableCell>{formatDuration(row.active_seconds)}</TableCell>
                          <TableCell>{formatDuration(row.idle_seconds)}</TableCell>
                          <TableCell>{formatTimestamp(row.first_activity)}</TableCell>
                          <TableCell>{formatTimestamp(row.last_activity)}</TableCell>
                          <TableCell>{row.most_used_app || '—'}</TableCell>
                          <TableCell>{actions.total ?? '—'}</TableCell>
                          <TableCell>{row.snapshot_count ?? 0}</TableCell>
                        </>
                      )}
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={summaryColumns.length + 1} sx={{ py: 0, borderBottom: expanded ? undefined : 0 }}>
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 1 }}>
                            {groupBy === 'employee' && row.productivity && (
                              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>Productivity (factual)</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Active Work Ratio {row.productivity.active_work_ratio ?? '—'}% ·
                                  Idle {row.productivity.idle_ratio ?? '—'}% ·
                                  Locked {row.productivity.locked_ratio ?? '—'}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  Productive {formatDuration(row.productivity.productive_seconds)} ·
                                  Neutral {formatDuration(row.productivity.neutral_seconds)} ·
                                  Non-productive {formatDuration(row.productivity.non_productive_seconds)} ·
                                  Unclassified {formatDuration(row.productivity.unclassified_seconds)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  Classification coverage {row.productivity.classification_coverage ?? '—'}%
                                  {row.productivity.productive_app_ratio != null
                                    ? ` · Productive app/domain ratio ${row.productivity.productive_app_ratio}%`
                                    : ''}
                                </Typography>
                                {row.productivity.score_enabled ? (
                                  row.productivity.score_available ? (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="subtitle2">
                                        {report?.today_so_far ? 'Productivity Score – Today So Far' : 'Productivity Score'}{' '}
                                        {row.productivity.score}% {row.productivity.score_band_label || ''}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        Attendance {row.productivity.components?.attendance?.score ?? '—'} ·
                                        Active Work {row.productivity.components?.active_work?.score ?? '—'} ·
                                        Productive Apps/Web {row.productivity.components?.productive_apps?.score ?? '—'} ·
                                        Schedule Compliance {row.productivity.components?.schedule_compliance?.score ?? '—'}
                                      </Typography>
                                      {(row.productivity.explanation || []).length > 0 && (
                                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                          Why: {(row.productivity.explanation || []).join(' ')}
                                        </Typography>
                                      )}
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                                      Productivity Score: Not enough data
                                      {row.productivity.unavailable_reason
                                        ? ` (${row.productivity.unavailable_reason})`
                                        : ''}
                                    </Typography>
                                  )
                                ) : (
                                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                    Composite Productivity Score is disabled (facts only).
                                  </Typography>
                                )}
                              </Box>
                            )}
                            <Typography variant="subtitle2" gutterBottom>Applications</Typography>
                            {(row.applications || []).length === 0 ? (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No application rollup.</Typography>
                            ) : (
                              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                                {(row.applications || []).slice(0, 10).map((app: any) => (
                                  <Chip
                                    key={app.key}
                                    size="small"
                                    label={`${app.key} · ${formatDuration(app.active_seconds)}`}
                                  />
                                ))}
                              </Box>
                            )}
                            <Typography variant="subtitle2" gutterBottom>Domains</Typography>
                            {(row.domains || []).length === 0 ? (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No domain rollup.</Typography>
                            ) : (
                              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                                {(row.domains || []).slice(0, 10).map((domain: any) => (
                                  <Chip
                                    key={domain.key}
                                    size="small"
                                    label={`${domain.key} · ${formatDuration(domain.active_seconds)}`}
                                  />
                                ))}
                              </Box>
                            )}
                            <Typography variant="subtitle2" gutterBottom>Actions</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {[
                                actions.clicks != null ? `Clicks ${actions.clicks}` : null,
                                actions.navigation != null ? `Navigation ${actions.navigation}` : null,
                                actions.app_switches != null ? `App switches ${actions.app_switches}` : null,
                                actions.window_changes != null ? `Window changes ${actions.window_changes}` : null,
                                actions.teks_actions != null ? `TEKS actions ${actions.teks_actions}` : null,
                              ].filter(Boolean).join(' · ') || 'No action breakdown.'}
                            </Typography>
                            {groupBy === 'computer' && Array.isArray(row.employees) && row.employees.length > 0 && (
                              <>
                                <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>Employees on this computer</Typography>
                                {row.employees.map((employee: any) => (
                                  <Typography key={employee.employee_id} variant="body2">
                                    {employee.employee_name || employee.employee_id} · {employee.action_count} actions · {formatTimestamp(employee.first_activity)} – {formatTimestamp(employee.last_activity)}
                                  </Typography>
                                ))}
                              </>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Email Preview</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {previewHtml ? (
            <Box
              component="iframe"
              title="Daily workforce report email preview"
              srcDoc={previewHtml}
              sx={{ width: '100%', height: '70vh', border: 0 }}
            />
          ) : (
            <Box sx={{ p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sendConfirmOpen} onClose={() => !sendMutation.isPending && setSendConfirmOpen(false)}>
        <DialogTitle>{emailAlreadySent ? 'Resend Report Email?' : 'Send Report Email?'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {emailAlreadySent
              ? `This will resend the daily workforce report for ${reportDate} to configured recipients.`
              : `Send the daily workforce report for ${reportDate} to configured recipients?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendConfirmOpen(false)} disabled={sendMutation.isPending}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => sendMutation.mutate()}
            disabled={sendMutation.isPending}
          >
            {sendMutation.isPending ? 'Sending…' : emailAlreadySent ? 'Resend' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ReportsPage: React.FC = () => {
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canReadRemoteSupport = permissions.includes('monitoring.remote_control.read')
    || permissions.includes('monitoring.remote_control.start');
  const [tab, setTab] = useState<'daily' | 'payroll' | 'remote-support'>('daily');

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Workforce Monitoring Reports</Typography>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab value="daily" label="Daily Monitoring Report" />
        <Tab value="payroll" label="Work Session Reconciliation" />
        {canReadRemoteSupport && (
          <Tab value="remote-support" label="Remote Support Sessions" />
        )}
      </Tabs>
      {tab === 'daily' && <DailyActivityTab />}
      {tab === 'payroll' && <PayrollReconciliationTab />}
      {tab === 'remote-support' && canReadRemoteSupport && <RemoteSupportSessionsTab />}
    </Box>
  );
};

export default ReportsPage;
